/**
 * Die Startseite (FR-ON-05).
 *
 * Was laeuft, steht oben. Der Code-Eingang ist gleich gross wie die Liste,
 * weil die Haelfte der Besucher ueber einen Aufsteller kommt und nicht
 * ueber die Agenda.
 */
import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { Footer } from '../components/Footer'
import { Podcast } from '../components/Podcast'
import { useLang } from '../lang'
import { api, ensureSession, loadSession } from '../lib/api'

interface RoomRow {
  id: string
  title: string
  status: string
  startsAt?: string | null
  language?: string
  players?: number
}

interface OffenerRaum {
  room_id: string
  title: string
  status: string
  players: number
  starts_at?: string | null
}

interface EventPayload {
  event: { id: string; slug: string; name: string; booth?: string } | null
  rooms: RoomRow[]
}

export function Start() {
  const { t } = useLang()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const unbekannt = params.get('unknown') === '1'
  const [data, setData] = useState<EventPayload | null>(null)
  const [offene, setOffene] = useState<OffenerRaum[]>([])
  const [code, setCode] = useState('')
  const [codeOffen, setCodeOffen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // Der Zugang zur Verwaltung steht nur dort, wo er auch etwas nuetzt.
  const sitzung = loadSession()
  const rollen = sitzung?.roles ?? []
  const darfVerwalten = rollen.includes('admin') || rollen.includes('manager')
  const angemeldet = Boolean(sitzung) && sitzung?.is_guest === false

  useEffect(() => {
    // Die Gast-Session zuerst: ohne sie antwortet jeder Knopf auf dieser
    // Seite mit 401 (FR-ON-02).
    void ensureSession().catch(() => undefined)
    api
      .get<EventPayload>('/events/current')
      .then(setData)
      .catch(() => setData({ event: null, rooms: [] }))
    // Das Verzeichnis ist unabhaengig vom Event: ein oeffentlicher Raum,
    // den jemand selbst aufgemacht hat, gehoert genauso hierher.
    api
      .get<{ rooms: OffenerRaum[] }>('/rooms/public')
      .then((r) => setOffene(r.rooms))
      .catch(() => setOffene([]))
  }, [])

  async function join(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const room = await api.get<{ room_id: string }>(`/rooms/by-code/${encodeURIComponent(code.trim())}`)
      navigate(`/room/${room.room_id}`)
    } catch (err) {
      setError((err as { code?: string }).code ?? 'generic')
    } finally {
      setBusy(false)
    }
  }

  // Was laeuft, steht oben; der Rest nach Startzeit (FR-ON-05).
  const rang: Record<string, number> = { running: 0, open: 1, planned: 2, paused: 3 }
  const ausEvent: RoomRow[] = (data?.rooms ?? []).filter((r) => r.status in rang)
  const ausVerzeichnis: RoomRow[] = offene.map((r) => ({
    id: r.room_id,
    title: r.title,
    status: r.status,
    startsAt: r.starts_at ?? null,
    players: r.players,
  }))
  // Ein Raum kann in beiden Listen stehen. Der Eintrag aus dem Verzeichnis
  // gewinnt, weil nur der die Teilnehmerzahl kennt.
  const bekannt = new Set(ausVerzeichnis.map((r) => r.id))
  const oeffentlich = [...ausVerzeichnis, ...ausEvent.filter((r) => !bekannt.has(r.id))].sort(
    (a, b) => (rang[a.status] ?? 9) - (rang[b.status] ?? 9),
  )

  async function soloStarten() {
    setBusy(true)
    try {
      const r = await api.post<{ room_id: string }>('/rooms/practice')
      navigate(`/room/${r.room_id}`)
    } catch (err) {
      setError((err as { code?: string }).code ?? 'generic')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="page stack">
      <div>
        <h1>
          Bullshit<br />Bingo<span className="dot">.</span>
        </h1>
        <p className="muted" style={{ marginTop: 6 }}>{t('app.tagline')}</p>
      </div>

      {unbekannt ? <div className="banner">{t('start.unknownCode')}</div> : null}

      {oeffentlich.length > 0 ? (
        <section>
          <div className="kicker">{t('start.public')}</div>
          <ul className="list">
            {oeffentlich.map((r) => (
              <li key={r.id}>
                {r.status === 'running' ? <span className="tag tag--live">live</span> : null}
                <Link to={`/room/${r.id}`} style={{ flex: 1 }}>{r.title}</Link>
                <span className="small muted">
                  {typeof r.players === 'number' && r.players > 0
                    ? t('start.players', { n: r.players })
                    : r.status !== 'running' && r.startsAt
                    ? new Date(r.startsAt).toLocaleTimeString(undefined, {
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : ''}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {data && oeffentlich.length === 0 ? (
        <p className="muted">{data.event ? t('start.noRooms') : t('start.noEvent')}</p>
      ) : null}

      {/* Vier Wege hinein, als vier Knoepfe. Vorher lagen sie in drei
          verschiedenen Kaesten und sahen aus wie verschiedene Dinge. */}
      <section className="doors">
        <button type="button" className="btn door door--main" disabled={busy}
          onClick={() => void soloStarten()}>
          <span className="door__title">{t('start.solo')}</span>
          <span className="door__hint">{t('start.soloHint')}</span>
        </button>

        <button type="button" className="btn btn--ghost door" aria-expanded={codeOffen}
          onClick={() => setCodeOffen((v) => !v)}>
          <span className="door__title">{t('start.join')}</span>
          <span className="door__hint">{t('start.joinHint')}</span>
        </button>

        {codeOffen ? (
          <form className="card stack" onSubmit={join}>
            <div>
              <label htmlFor="code">{t('start.joinHint')}</label>
              <input id="code" autoFocus value={code} onChange={(e) => setCode(e.target.value)}
                placeholder="BSB-XK7T" autoComplete="off" autoCapitalize="characters"
                spellCheck={false}
                style={{ fontFamily: 'var(--font-mono)', letterSpacing: '0.1em' }} />
            </div>
            {error ? (
              <p className="small" style={{ color: 'var(--red)' }}>
                {t(`err.${error}` as 'err.generic')}
              </p>
            ) : null}
            <button className="btn btn--block" disabled={busy || code.trim().length < 4}>
              {busy ? t('common.loading') : t('start.join')}
            </button>
          </form>
        ) : null}

        <Link className="btn btn--ghost door" to="/erstellen">
          <span className="door__title">{t('start.create')}</span>
          <span className="door__hint">{t('start.createHint')}</span>
        </Link>

        <Link className="btn btn--ghost door" to="/konto">
          <span className="door__title">
            {angemeldet ? t('start.myStuff') : t('start.signIn')}
          </span>
          <span className="door__hint">
            {angemeldet ? t('start.accountHave') : t('start.accountWhy')}
          </span>
        </Link>
      </section>

      <Podcast />

      <div className="row row--between small">
        <Link to="/regeln">{t('start.rules')}</Link>
        {darfVerwalten ? <Link to="/verwaltung">{t('admin.title')}</Link> : null}
      </div>

      <Footer />
    </div>
  )
}
